/**
 * Layout System for Cell Swap Game
 * 
 * Centralizes all layout calculations for HUD arrangements on iOS landscape phones and portrait devices.
 * Supports A/B testing different HUD arrangements without hardcoded pixel positions.
 */

export type LayoutMode = 'classicSidebars' | 'topBottomHUD' | 'overlayHUD' | 'portraitTopBottomHUD'

// Grid constants
const GRID_COLUMNS = 6
const GRID_ROWS = 12
const CELL_SIZE = 96
const GRID_WIDTH = GRID_COLUMNS * CELL_SIZE // 576px
const GRID_HEIGHT = GRID_ROWS * CELL_SIZE // 1152px

export interface SafeAreaInsets {
  top: number
  right: number
  bottom: number
  left: number
}

export interface LayoutBounds {
  x: number
  y: number
  width: number
  height: number
}

export interface ComputedLayout {
  mode: LayoutMode
  screen: LayoutBounds
  safeArea: SafeAreaInsets
  grid: {
    x: number
    y: number
    scale: number
    bounds: LayoutBounds
  }
  hud: {
    position: { x: number; y: number }
    scale: number
    width: number
    height: number
    bounds: LayoutBounds
  }
  legend: {
    position: { x: number; y: number }
    scale: number
    width: number
    height: number
    bounds: LayoutBounds
  }
  pauseButton: {
    x: number
    y: number
  }
}

/**
 * Get safe area insets for iOS devices
 */
export function getSafeAreaInsets(): SafeAreaInsets {
  if (typeof window === 'undefined') {
    return { top: 0, right: 0, bottom: 0, left: 0 }
  }

  // Try to get CSS environment variables for safe area
  const getEnv = (variable: string): number => {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(`env(${variable})`)
    return parseInt(value) || 0
  }

  return {
    top: getEnv('safe-area-inset-top'),
    right: getEnv('safe-area-inset-right'),
    bottom: getEnv('safe-area-inset-bottom'),
    left: getEnv('safe-area-inset-left')
  }
}

/**
 * Compute layout for all UI elements based on mode and screen size
 */
export function computeLayout(
  width: number,
  height: number,
  mode: LayoutMode,
  safeArea?: SafeAreaInsets
): ComputedLayout {
  const insets = safeArea || getSafeAreaInsets()
  const aspectRatio = width / height
  const isLandscape = aspectRatio > 1
  const isMobile = width < 768

  const screen: LayoutBounds = {
    x: 0,
    y: 0,
    width,
    height
  }

  // Default layout for classicSidebars (current implementation)
  if (mode === 'classicSidebars') {
    return computeClassicSidebarsLayout(screen, insets, isLandscape, isMobile)
  } else if (mode === 'topBottomHUD') {
    return computeTopBottomLayout(screen, insets, isLandscape, isMobile)
  } else if (mode === 'overlayHUD') {
    return computeOverlayLayout(screen, insets, isLandscape, isMobile)
  } else if (mode === 'portraitTopBottomHUD') {
    return computePortraitTopBottomLayout(screen, insets)
  }

  // Fallback to classic
  return computeClassicSidebarsLayout(screen, insets, isLandscape, isMobile)
}

/**
 * Classic sidebars layout: HUD on right, Legend on left, grid in center
 */
function computeClassicSidebarsLayout(
  screen: LayoutBounds,
  insets: SafeAreaInsets,
  isLandscape: boolean,
  isMobile: boolean
): ComputedLayout {
  const hudWidth = 280
  const hudHeight = Math.min(screen.height * 0.9, 600)
  const legendWidth = 300
  const legendHeight = Math.min(screen.height * 0.9, 600)

  let gridX = screen.width / 2
  let gridY = screen.height / 2
  let gridScale = 1
  let hudPos = { x: 0, y: 0 }
  let legendPos = { x: 0, y: 0 }

  if (isLandscape && !isMobile) {
    // Desktop landscape: sidebars on left and right
    const sidebarPadding = Math.max(insets.left, 20) + Math.max(insets.right, 20)
    const availableGridWidth = screen.width - 620 - sidebarPadding
    gridX = 320 + Math.max(insets.left, 20) + availableGridWidth / 2
    const scaleX = availableGridWidth / GRID_WIDTH
    const scaleY = screen.height / GRID_HEIGHT
    gridScale = Math.floor(Math.min(scaleX, scaleY) * 0.85 * 100) / 100

    hudPos = { x: screen.width - hudWidth - Math.max(insets.right, 10), y: 20 }
    legendPos = { x: 20 + Math.max(insets.left, 0), y: 20 }
  } else if (isLandscape && isMobile) {
    // Mobile landscape: compact sidebars
    const sidebarPadding = Math.max(insets.left, 10) + Math.max(insets.right, 10)
    const availableGridWidth = screen.width - 440 - sidebarPadding
    gridX = 230 + Math.max(insets.left, 10) + availableGridWidth / 2
    const scaleX = availableGridWidth / GRID_WIDTH
    const scaleY = (screen.height - 100) / GRID_HEIGHT
    gridScale = Math.floor(Math.min(scaleX, scaleY) * 0.85 * 100) / 100

    hudPos = { x: screen.width - 200 - Math.max(insets.right, 10), y: 20 }
    legendPos = { x: 10 + Math.max(insets.left, 0), y: 20 }
  } else {
    // Portrait mode
    const margin = Math.max(insets.left, 10)
    const availableWidth = screen.width - 2 * margin
    gridX = screen.width / 2
    gridY = screen.height * 0.55
    const scaleX = availableWidth / GRID_WIDTH
    const scaleY = (screen.height * 0.7) / GRID_HEIGHT
    gridScale = Math.floor(Math.min(scaleX, scaleY) * 100) / 100

    hudPos = { x: screen.width - 200 - margin, y: 20 }
    legendPos = { x: margin, y: 20 }
  }

  const gridBounds: LayoutBounds = {
    x: gridX - (GRID_WIDTH * gridScale) / 2,
    y: gridY - (GRID_HEIGHT * gridScale) / 2,
    width: GRID_WIDTH * gridScale,
    height: GRID_HEIGHT * gridScale
  }

  const hudBounds: LayoutBounds = {
    x: hudPos.x,
    y: hudPos.y,
    width: hudWidth,
    height: hudHeight
  }

  const legendBounds: LayoutBounds = {
    x: legendPos.x,
    y: legendPos.y,
    width: legendWidth,
    height: legendHeight
  }

  return {
    mode: 'classicSidebars',
    screen,
    safeArea: insets,
    grid: {
      x: gridX,
      y: gridY,
      scale: gridScale,
      bounds: gridBounds
    },
    hud: {
      position: hudPos,
      scale: 1.0,
      width: hudWidth,
      height: hudHeight,
      bounds: hudBounds
    },
    legend: {
      position: legendPos,
      scale: 1.0,
      width: legendWidth,
      height: legendHeight,
      bounds: legendBounds
    },
    pauseButton: {
      x: screen.width / 2,
      y: Math.max(insets.top + 15, 20)
    }
  }
}

/**
 * Top/Bottom HUD layout: HUD at top, Legend at bottom, grid in center
 * Optimized for landscape iPhone screens with maximum grid size
 */
function computeTopBottomLayout(
  screen: LayoutBounds,
  insets: SafeAreaInsets,
  isLandscape: boolean,
  isMobile: boolean
): ComputedLayout {
  // Thin bands for top and bottom HUD elements
  const hudHeight = 65
  const hudWidth = screen.width // Full width for horizontal layout
  const legendHeight = 70
  const legendWidth = screen.width // Full width for horizontal layout

  // Consistent padding respecting safe areas
  const topPadding = Math.max(insets.top, 12)
  const bottomPadding = Math.max(insets.bottom, 12)
  const sidePadding = Math.max(insets.left, insets.right, 12)

  // Position HUD at top - full width
  const hudPos = {
    x: 0,
    y: topPadding
  }

  // Position legend at bottom - full width
  const legendPos = {
    x: 0,
    y: screen.height - legendHeight - bottomPadding
  }

  // Grid in center with minimal padding for maximum size
  const gridPaddingTop = hudHeight + topPadding + 15
  const gridPaddingBottom = legendHeight + bottomPadding + 15
  const availableHeight = screen.height - gridPaddingTop - gridPaddingBottom
  const availableWidth = screen.width - (sidePadding * 2)

  // Maximize grid scale without the 0.85 multiplier for larger grid
  const gridScale = Math.min(availableWidth / GRID_WIDTH, availableHeight / GRID_HEIGHT)
  const gridX = screen.width / 2
  const gridY = gridPaddingTop + availableHeight / 2

  const gridBounds: LayoutBounds = {
    x: gridX - (GRID_WIDTH * gridScale) / 2,
    y: gridY - (GRID_HEIGHT * gridScale) / 2,
    width: GRID_WIDTH * gridScale,
    height: GRID_HEIGHT * gridScale
  }

  const hudBounds: LayoutBounds = {
    x: hudPos.x,
    y: hudPos.y,
    width: hudWidth,
    height: hudHeight
  }

  const legendBounds: LayoutBounds = {
    x: legendPos.x,
    y: legendPos.y,
    width: legendWidth,
    height: legendHeight
  }

  return {
    mode: 'topBottomHUD',
    screen,
    safeArea: insets,
    grid: {
      x: gridX,
      y: gridY,
      scale: gridScale,
      bounds: gridBounds
    },
    hud: {
      position: hudPos,
      scale: 1.0,
      width: hudWidth,
      height: hudHeight,
      bounds: hudBounds
    },
    legend: {
      position: legendPos,
      scale: 1.0,
      width: legendWidth,
      height: legendHeight,
      bounds: legendBounds
    },
    pauseButton: {
      x: screen.width - 60, // Position in top-right of top band
      y: topPadding + hudHeight / 2 // Vertically centered in top band
    }
  }
}

/**
 * Overlay HUD layout: Translucent HUDs overlay the grid corners
 * Grid goes near edge-to-edge for maximum size
 */
function computeOverlayLayout(
  screen: LayoutBounds,
  insets: SafeAreaInsets,
  isLandscape: boolean,
  isMobile: boolean
): ComputedLayout {
  const hudWidth = 240
  const hudHeight = 400
  const legendWidth = 240
  const legendHeight = 300

  // Minimize margins for near edge-to-edge grid - respect safe areas
  const horizontalMargin = Math.max(insets.left, insets.right, 10)
  const verticalMargin = Math.max(insets.top, insets.bottom, 10)
  
  // Grid centered and maximized since overlays float on top
  const availableWidth = screen.width - 2 * horizontalMargin
  const availableHeight = screen.height - 2 * verticalMargin
  
  // Use 0.98 multiplier to allow small breathing room while maximizing size
  const gridScale = Math.min(availableWidth / GRID_WIDTH, availableHeight / GRID_HEIGHT) * 0.98
  const gridX = screen.width / 2
  const gridY = screen.height / 2

  // HUD overlays top-right corner - keep clear of center playfield
  const hudPos = {
    x: screen.width - hudWidth - Math.max(insets.right, 15),
    y: Math.max(insets.top, 15)
  }

  // Legend overlays bottom-left corner - keep clear of center playfield
  const legendPos = {
    x: Math.max(insets.left, 15),
    y: screen.height - legendHeight - Math.max(insets.bottom, 15)
  }

  const gridBounds: LayoutBounds = {
    x: gridX - (GRID_WIDTH * gridScale) / 2,
    y: gridY - (GRID_HEIGHT * gridScale) / 2,
    width: GRID_WIDTH * gridScale,
    height: GRID_HEIGHT * gridScale
  }

  const hudBounds: LayoutBounds = {
    x: hudPos.x,
    y: hudPos.y,
    width: hudWidth,
    height: hudHeight
  }

  const legendBounds: LayoutBounds = {
    x: legendPos.x,
    y: legendPos.y,
    width: legendWidth,
    height: legendHeight
  }

  return {
    mode: 'overlayHUD',
    screen,
    safeArea: insets,
    grid: {
      x: gridX,
      y: gridY,
      scale: gridScale,
      bounds: gridBounds
    },
    hud: {
      position: hudPos,
      scale: 0.9, // Slightly smaller for overlay
      width: hudWidth,
      height: hudHeight,
      bounds: hudBounds
    },
    legend: {
      position: legendPos,
      scale: 0.9,
      width: legendWidth,
      height: legendHeight,
      bounds: legendBounds
    },
    pauseButton: {
      x: screen.width / 2,
      y: Math.max(insets.top + 10, 15)
    }
  }
}

/**
 * Portrait Top/Bottom HUD layout: Optimized for portrait orientation with maximum grid size
 * Top band: Score, level, pause/info buttons - all horizontally aligned
 * Bottom band: Next Up pieces (left-aligned), combo meter (right-aligned)
 * Grid: Maximum size between bands, centered with square cells, visually prominent
 */
function computePortraitTopBottomLayout(
  screen: LayoutBounds,
  insets: SafeAreaInsets
): ComputedLayout {
  // Compact HUD heights to maximize grid real estate
  const topHudHeight = 80 // Slightly taller for better alignment and spacing
  const bottomHudHeight = 100 // Taller to fully contain Next Up cell icons
  const hudWidth = screen.width // Full width

  // Safe area compliance: respect iOS notches (Dynamic Island on newer devices, notch on older)
  // Since status bar is hidden, safe area top includes notch/island area
  const topSafeInset = Math.max(insets.top, 0) // Safe area for top notch/island
  const bottomSafeInset = Math.max(insets.bottom, 0) // Safe area for home indicator
  const sidePadding = Math.max(insets.left, insets.right, 10) // Minimal side padding

  // Position top HUD below the safe area with small buffer for visual breathing room
  const hudPos = {
    x: 0,
    y: topSafeInset + 8 // Small buffer below top safe area
  }

  // Position bottom HUD above the safe area with small buffer
  const legendPos = {
    x: 0,
    y: screen.height - bottomHudHeight - bottomSafeInset - 8 // Small buffer above home indicator
  }

  // Grid in center with optimized padding for maximum prominence
  const gridPaddingTop = hudPos.y + topHudHeight + 12 // Tight spacing from top HUD
  const gridPaddingBottom = bottomHudHeight + bottomSafeInset + 20 // Slightly more space from bottom
  const availableHeight = screen.height - gridPaddingTop - gridPaddingBottom
  const availableWidth = screen.width - (sidePadding * 2)

  // Maximize grid scale for portrait prominence
  const gridScale = Math.min(availableWidth / GRID_WIDTH, availableHeight / GRID_HEIGHT)
  const gridX = screen.width / 2
  // Center grid vertically in available space
  const gridY = gridPaddingTop + availableHeight / 2

  const gridBounds: LayoutBounds = {
    x: gridX - (GRID_WIDTH * gridScale) / 2,
    y: gridY - (GRID_HEIGHT * gridScale) / 2,
    width: GRID_WIDTH * gridScale,
    height: GRID_HEIGHT * gridScale
  }

  const hudBounds: LayoutBounds = {
    x: hudPos.x,
    y: hudPos.y,
    width: hudWidth,
    height: topHudHeight
  }

  const legendBounds: LayoutBounds = {
    x: legendPos.x,
    y: legendPos.y,
    width: hudWidth,
    height: bottomHudHeight
  }

  // Pause button in top-right with >= 44px hit target
  // Position with comfortable tap margin from edge (minimum 44px clearance)
  const pauseButtonMargin = 60 // Distance from right edge for comfortable tapping
  const pauseButtonX = screen.width - pauseButtonMargin
  const pauseButtonY = hudPos.y + topHudHeight / 2

  return {
    mode: 'portraitTopBottomHUD',
    screen,
    safeArea: insets,
    grid: {
      x: gridX,
      y: gridY,
      scale: gridScale,
      bounds: gridBounds
    },
    hud: {
      position: hudPos,
      scale: 1.0,
      width: hudWidth,
      height: topHudHeight,
      bounds: hudBounds
    },
    legend: {
      position: legendPos,
      scale: 1.0,
      width: hudWidth,
      height: bottomHudHeight,
      bounds: legendBounds
    },
    pauseButton: {
      x: pauseButtonX, // Right-aligned with >= 44px hit target
      y: pauseButtonY // Vertically centered in top band
    }
  }
}
